import { B2B_AGREEMENT_ID_STORAGE_KEY } from '@telialt/utils/src/authentication/constants';
import { getSegment, TeliaSegment } from '@telialt/utils/src/segment';

import { NavigationResponseData, UserMenuItemType } from './model';
import { getIsContactsAndPrivacyPath, getIsMyAccountPath, getIsSelfService } from './utils/path';

export const NAVIGATION_CONTEXT_BAR_HEIGHT = 48;
export const NAVIGATION_MAIN_BAR_HEIGHT = 72;
export const NAVIGATION_HEIGHT = NAVIGATION_CONTEXT_BAR_HEIGHT + NAVIGATION_MAIN_BAR_HEIGHT;

export const NAVIGATION_MAX_MENU_LEVELS = 3;

export const PATHS = {
    LOGO: {
        public: {
            [TeliaSegment.B2c]: '/privatiems',
            [TeliaSegment.B2b]: '/verslui',
            [TeliaSegment.English]: '/eng',
            [TeliaSegment.Russian]: '/rus',
        },
        selfService: {
            [TeliaSegment.B2c]: '/mano/privatiems/apzvalga',
            [TeliaSegment.B2b]: '/mano/verslui/apzvalga',
            [TeliaSegment.Fix]: '/mano/paskyra',
        },
    },
    SEGMENT: {
        public: {
            [TeliaSegment.B2c]: '/privatiems',
            [TeliaSegment.B2b]: '/verslui',
        },
        selfService: {
            [TeliaSegment.B2c]: '/mano/privatiems/apzvalga',
            [TeliaSegment.B2b]: '/mano/verslui/apzvalga',
            [TeliaSegment.Fix]: '/mano/paskyra/prisijungtiverslui',
        },
        [TeliaSegment.Fix]: {
            dashboard: '/mano/paskyra/Default.aspx',
            noBusinessAgreements: '/mano/paskyra/NoBusinessAgreements',
        },
    },
    LOGIN: {
        [TeliaSegment.B2c]: '/mano/privatiems/sso',
        [TeliaSegment.B2b]: '/mano/verslui/sso',
    },
    IDENTIFICATION: {
        [TeliaSegment.B2b]: '/mano/verslui/apzvalga/identifikuoti',
    },
    MY_ACCOUNT: {
        [TeliaSegment.B2c]: '/mano/privatiems/paskyra',
        [TeliaSegment.B2b]: '/mano/verslui/paskyra',
        [TeliaSegment.Fix]: '/mano/verslui/paskyra',
    },
    CONTACTS_PRIVACY: {
        [TeliaSegment.B2c]: '/mano/privatiems/kontaktai-ir-privatumas',
        [TeliaSegment.B2b]: '/mano/verslui/kontaktai-ir-privatumas',
        [TeliaSegment.Fix]: '/mano/verslui/kontaktai-ir-privatumas',
    },
    USER_AREA: {
        logout: '/logout',
    },
};

export const SELF_SERVICE_PATH_PREFIX = '/mano/';
export const UPSELL_OFFER_URL_STORAGE_KEY = 'UP_SELL_URL';

export const QUERY_KEYS = {
    NAVIGATION: 'navigation',
    MAINTENANCE: 'maintenance',
    PENDING_DOCUMENTS: 'document-list-pending',
    AGREEMENT_LIST: 'agreeement-list',
    SIGNING_IN_PROGRESS_ORDER: 'signing-in-progress-order-exist',
};

export const USER_MENU_ITEM_IDS = {
    B2C: 'b2c',
    B2B: 'b2b',
    FIX: 'fix',
    CONTACTS_PRIVACY: 'contacts_privacy',
    MY_ACCOUNT: 'my_account',
    LOGOUT: 'logout',
};

export const getFallbackData = (layoutPath: string): NavigationResponseData => {
    const segment = getSegment(layoutPath);

    const navMainRightMenu: NavigationResponseData['navMainRightMenu'] = [
        {
            title: (() => {
                if (segment === TeliaSegment.Russian) {
                    return 'Поиск';
                } else if (segment === TeliaSegment.English) {
                    return 'Search';
                }

                return 'Ieškoti';
            })(),
            icon: 'search',
            component: 'SEARCH',
        },
    ];

    if (segment === TeliaSegment.B2b || segment === TeliaSegment.Fix || segment === TeliaSegment.B2c) {
        navMainRightMenu.push({
            title: 'Prisijungti',
            icon: 'login',
            component: 'USER',
        });
    }

    return { navMainRightMenu };
};

export const getUserMenuItems = (isStrong: boolean, segment: TeliaSegment, currentPath: string): UserMenuItemType[] => {
    const isWeakUserB2B = segment === TeliaSegment.B2b && !isStrong;
    const isAccountOrContactsAndPrivacyPath =
        getIsMyAccountPath(currentPath) || getIsContactsAndPrivacyPath(currentPath);

    return [
        {
            id: USER_MENU_ITEM_IDS.B2C,
            icon: 'end-user' as const,
            url: PATHS.SEGMENT.selfService[TeliaSegment.B2c],
            isActive:
                getIsSelfService(currentPath) && !isAccountOrContactsAndPrivacyPath && segment === TeliaSegment.B2c,
        },
        {
            id: USER_MENU_ITEM_IDS.B2B,
            icon: 'smartphone' as const,
            url: PATHS.SEGMENT.selfService[TeliaSegment.B2b],
            isActive:
                getIsSelfService(currentPath) && !isAccountOrContactsAndPrivacyPath && segment === TeliaSegment.B2b,
        },
        {
            id: USER_MENU_ITEM_IDS.FIX,
            icon: 'suitcase' as const,
            url: PATHS.SEGMENT.selfService[TeliaSegment.Fix],
            isActive: segment === TeliaSegment.Fix,
        },
        ...(!isWeakUserB2B
            ? [
                  {
                      id: USER_MENU_ITEM_IDS.CONTACTS_PRIVACY,
                      icon: 'settings' as const,
                      url: PATHS.CONTACTS_PRIVACY[segment],
                      openInNewTab: true,
                      isActive: getIsContactsAndPrivacyPath(currentPath),
                  },
              ]
            : []),
        {
            id: USER_MENU_ITEM_IDS.MY_ACCOUNT,
            icon: 'portal' as const,
            url: PATHS.MY_ACCOUNT[segment],
            openInNewTab: true,
            isActive: getIsMyAccountPath(currentPath),
        },
        {
            id: USER_MENU_ITEM_IDS.LOGOUT,
            icon: 'logout' as const,
            url: PATHS.USER_AREA.logout,
        },
    ];
};

export const getAgreementId = () => {
    return (typeof window !== 'undefined' && window.sessionStorage.getItem(B2B_AGREEMENT_ID_STORAGE_KEY)) || '';
};
